import { cx } from '@emotion/css'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/system'
import { elementType, oneOf, string, node } from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import Link from '../link'
import { IconWrapper, Label, Root, BadgeNotification, LinkStyled } from './floating-action.styled'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'

const FloatingAction = ({
	className,
	text,
	position,
	href,
	style,
	Icon,
	onCloseRef,
	onOpenRef,
	notificationBackgroundColor,
	notificationContent,
	...props
}) => {
	const theme = useTheme()

	const buttonRef = useRef()
	const iconRef = useRef()
	const labelRef = useRef()

	const [state, setState] = useState({
		opacity: 0,
		hover: 0,
		resWidth: 44,
		labelShift: 0,
	})

	const acceptHover = useMediaQuery(theme.breakpoints.up('md')) && text

	const vars = useRef({
		height: 0,
		textWidth: 0,
		iconWidth: 44,
	})

	useEffect(() => {
		if (buttonRef?.current) {
			vars.current = {
				height: buttonRef.current.clientHeight,
				textWidth: text ? labelRef.current.clientWidth : 0,
				iconWidth: iconRef.current.clientWidth,
			}

			setState({
				opacity: 1,
				resWidth: vars.current.iconWidth,
				labelShift: vars.current.textWidth * -1,
				hover: 0,
			})
		}
	}, [text])

	const {
		floatingActions: { singleWidget },
	} = useConfig()

	const handleHover = (isHovered) => {
		if (isHovered) {
			const iconLPadding = window.getComputedStyle(iconRef.current).getPropertyValue(`padding-${position}`)
			setState({
				...state,
				...{
					hover: 1,
					labelShift: vars.current.iconWidth,
					resWidth: vars.current.textWidth + vars.current.iconWidth + parseInt(iconLPadding, 10),
				},
			})
		} else {
			setState({
				...state,
				...{
					hover: 0,
					labelShift: vars.current.textWidth * -1,
					resWidth: vars.current.iconWidth,
				},
			})
		}
	}
	useEffect(() => {
		if (onCloseRef) {
			onCloseRef.current = () =>
				setState({
					opacity: 1,
					resWidth: vars.current.iconWidth,
					labelShift: vars.current.textWidth * -1,
				})
		}
		if (onOpenRef) {
			onOpenRef.current = () =>
				setState({
					opacity: 0,
					resWidth: vars.current.iconWidth,
					labelShift: vars.current.textWidth * -1,
				})
		}
	}, [onCloseRef, onOpenRef])

	return !singleWidget ? (
		<Root
			ref={buttonRef}
			className={cx(state.opacity ? 'faLauncherActive' : 'faLauncherInactive', className)}
			onMouseEnter={acceptHover ? () => handleHover(true) : null}
			onMouseLeave={acceptHover ? () => handleHover(false) : null}
			position={position}
			style={{
				...style,
				width: state.resWidth + 'px',
			}}
			{...props}
		>
			<BadgeNotification badgeContent={notificationContent} notificationBackgroundColor={notificationBackgroundColor}>
				<Link href={href} target="_blank">
					<IconWrapper ref={iconRef}>
						<Icon />
					</IconWrapper>
					{text && (
						<Label component="div" style={{ [position]: state.labelShift }} ref={labelRef}>
							{text}
						</Label>
					)}
				</Link>
			</BadgeNotification>
		</Root>
	) : (
		<LinkStyled href={href} target="_blank" {...props}>
			<IconWrapper ref={iconRef}>
				<Icon />
			</IconWrapper>
			{text && (
				<Label component="div" ref={labelRef}>
					{text}
				</Label>
			)}
		</LinkStyled>
	)
}

FloatingAction.defaultProps = {
	position: 'left',
	style: {},
}

FloatingAction.propTypes = {
	text: string,
	textColor: string,
	backgroundColor: string,
	notificationBackgroundColor: string,
	notificationContent: node,
	position: oneOf(['left', 'right']),
	Icon: elementType,
}

export default FloatingAction
